import React, { useState } from "react";
import { Check, SlidersHorizontal, Clock, MoreHorizontal, Trash2, UserPlus, Undo2 } from "lucide-react";
import {
  TYPE_OPTIONS, URGENCY_OPTIONS, HOUSEHOLD_DOMAIN_OPTIONS, TYPE_META, URGENCY_META, DOMAIN_META,
  OWNER_OPTIONS, OWNER_META, VISIBILITY_OPTIONS, VISIBILITY_META, handoffAction,
} from "../lib/meta";
import { useAuth } from "../contexts/AuthContext";
import SegmentedField from "./SegmentedField";
import { SnoozeChips } from "./SnoozeChips";
import { Sheet, SheetContent } from "./ui/sheet";
import { useIsMobile } from "../hooks/useIsMobile";

const opts = (arr, meta) => arr.map((v) => ({ value: v, label: meta[v].label }));
const actionCls =
  "inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold border transition-all duration-200";

// Mobile-first action hierarchy: primary Mark done, secondary Snooze,
// tertiary overflow (Adjust details / Delete). Adjust opens V4 segmented
// controls — no native <select>. Backend fields/behaviour unchanged.
export default function QuickEdit({ entry, onChange, onView, onSnooze, onDelete, onHandoff, snoozable }) {
  const { user } = useAuth();
  const partnerName = user?.partner?.name;
  const hasPartner = Boolean(user?.partner);
  const ownerOpts = OWNER_OPTIONS.map((o) => ({
    value: o, label: o === "partner" && partnerName ? partnerName : OWNER_META[o].label,
  }));
  const [panel, setPanel] = useState(null); // "adjust" | "snooze" | null
  const [menu, setMenu] = useState(false);
  const isMobile = useIsMobile();
  const done = entry.status === "DONE";
  const archived = entry.status === "ARCHIVED";
  const domainValue = entry.domain === "PROSPECT" ? "WORK" : entry.domain;

  // Same handoff action as the detail sheet, surfaced for discoverability. Solo
  // users (no partner) never see it; only active/non-archived items. State-aware
  // label comes from the shared helper so the menu and detail sheet never diverge.
  const canHandoff = hasPartner && !done && !archived && Boolean(onHandoff);
  const ho = handoffAction(entry, partnerName);
  const HandoffIcon = ho.kind === "give" ? UserPlus : Undo2;
  const triggerHandoff = () => {
    setMenu(false);
    if (onView) onView();
    onHandoff(entry.id, ho.action);
  };

  const openAdjust = () => { setMenu(false); if (onView) onView(); setPanel((p) => (p === "adjust" ? null : "adjust")); };

  return (
    <div data-testid={`quickedit-${entry.id}`}>
      <div className="flex items-center gap-2">
        {!archived && (
          <button
            data-testid={`btn-done-${entry.id}`}
            onClick={() => onChange(entry.id, { status: done ? "ACTIVE" : "DONE" })}
            className={`${actionCls} ${
              done
                ? "bg-[var(--pine-soft)] text-[var(--pine)] border-[var(--pine-line)]"
                : "bg-[var(--pine)] text-[var(--on-pine)] border-[var(--pine)] hover:bg-[var(--pine-dark)] shadow-[0_4px_14px_rgba(44,90,77,0.20)]"
            }`}
          >
            <Check size={14} strokeWidth={2} />
            {done ? "Done" : "Mark done"}
          </button>
        )}
        {snoozable && !done && !archived && (
          <button
            data-testid={`snooze-toggle-${entry.id}`}
            onClick={() => setPanel((p) => (p === "snooze" ? null : "snooze"))}
            className={`${actionCls} bg-[var(--surface)] text-[var(--ink2)] border-[var(--line)] hover:border-[var(--pine)] hover:text-[var(--pine)]`}
          >
            <Clock size={13} strokeWidth={2} />
            Snooze
          </button>
        )}

        <div className="relative ml-auto">
          <button
            data-testid={`item-more-${entry.id}`}
            onClick={() => setMenu((m) => !m)}
            className="h-9 w-9 rounded-full flex items-center justify-center text-[var(--ink4)] hover:text-[var(--pine)] hover:bg-[var(--line2)] transition-all duration-200"
            title="More actions"
          >
            <MoreHorizontal size={18} strokeWidth={2} />
          </button>
          {menu && !isMobile && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setMenu(false)} />
              <div className="absolute right-0 mt-1 z-30 w-48 rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_12px_32px_rgba(0,0,0,0.12)] p-1.5">
                {canHandoff && (
                  <button
                    data-testid={`handoff-${entry.id}`}
                    onClick={triggerHandoff}
                    className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-[var(--pine)] hover:bg-[var(--pine-soft)] transition-colors"
                  >
                    <HandoffIcon size={15} strokeWidth={1.9} /> {ho.label}
                  </button>
                )}
                <button
                  data-testid={`adjust-${entry.id}`}
                  onClick={openAdjust}
                  className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-[var(--ink2)] hover:bg-[var(--surface2)] transition-colors"
                >
                  <SlidersHorizontal size={15} strokeWidth={1.9} /> Adjust details
                </button>
                <button
                  data-testid={`btn-delete-${entry.id}`}
                  onClick={() => { setMenu(false); onDelete && onDelete(entry.id); }}
                  className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-[var(--danger)] hover:bg-[var(--danger-soft)] transition-colors"
                >
                  <Trash2 size={15} strokeWidth={1.9} /> Delete
                </button>
              </div>
            </>
          )}

          <Sheet open={menu && isMobile} onOpenChange={setMenu}>
            <SheetContent
              side="bottom"
              className="bg-[var(--surface)] border-t border-[var(--line)] rounded-t-3xl px-3 pt-2 sheet-safe-bottom"
              data-testid={`item-actions-sheet-${entry.id}`}
            >
              <div className="mx-auto w-full max-w-md pb-1">
                <div className="h-1 w-10 rounded-full bg-[var(--line)] mx-auto mt-1.5 mb-3" />
                <p className="px-3 pb-2 text-sm text-[var(--ink3)] line-clamp-1">{entry.title || "This item"}</p>
                <div className="space-y-1">
                  {canHandoff && (
                    <button
                      data-testid={`handoff-${entry.id}`}
                      onClick={triggerHandoff}
                      className="w-full flex items-center gap-3 rounded-2xl px-4 py-3.5 min-h-[52px] text-[15px] font-medium text-[var(--pine)] hover:bg-[var(--pine-soft)] transition-colors"
                    >
                      <HandoffIcon size={18} strokeWidth={1.9} /> {ho.label}
                    </button>
                  )}
                  <button
                    data-testid={`adjust-${entry.id}`}
                    onClick={openAdjust}
                    className="w-full flex items-center gap-3 rounded-2xl px-4 py-3.5 min-h-[52px] text-[15px] text-[var(--ink)] hover:bg-[var(--surface2)] transition-colors"
                  >
                    <SlidersHorizontal size={18} strokeWidth={1.9} /> Adjust details
                  </button>
                  <button
                    data-testid={`btn-delete-${entry.id}`}
                    onClick={() => { setMenu(false); onDelete && onDelete(entry.id); }}
                    className="w-full flex items-center gap-3 rounded-2xl px-4 py-3.5 min-h-[52px] text-[15px] text-[var(--danger)] hover:bg-[var(--danger-soft)] transition-colors"
                  >
                    <Trash2 size={18} strokeWidth={1.9} /> Delete
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {panel === "snooze" && onSnooze && (
        <div className="mt-3 pt-3 border-t border-[var(--line2)] rise-in">
          <SnoozeChips entryId={entry.id} onSnooze={(iso) => { onSnooze(iso); setPanel(null); }} />
        </div>
      )}

      {panel === "adjust" && (
        <div className="mt-3 pt-3 border-t border-[var(--line2)] rise-in space-y-3" data-testid={`adjust-panel-${entry.id}`}>
          <SegmentedField label="Type" testid={`edit-type-${entry.id}`} value={entry.type}
            options={opts(TYPE_OPTIONS, TYPE_META)} onChange={(v) => onChange(entry.id, { type: v })} />
          <SegmentedField label="Timing" testid={`edit-urgency-${entry.id}`} value={entry.urgency}
            options={opts(URGENCY_OPTIONS, URGENCY_META)} onChange={(v) => onChange(entry.id, { urgency: v })} />
          <SegmentedField label="Area" testid={`edit-domain-${entry.id}`} value={domainValue}
            options={opts(HOUSEHOLD_DOMAIN_OPTIONS, DOMAIN_META)} onChange={(v) => onChange(entry.id, { domain: v })} />
          <SegmentedField label="Owner" testid={`edit-owner-${entry.id}`} value={entry.owner || "me"}
            options={ownerOpts} onChange={(v) => onChange(entry.id, { owner: v })} />
          <SegmentedField label="Visibility" testid={`edit-visibility-${entry.id}`} value={entry.visibility || "private"}
            options={opts(VISIBILITY_OPTIONS, VISIBILITY_META)} onChange={(v) => onChange(entry.id, { visibility: v })} />
        </div>
      )}
    </div>
  );
}
